import { createSlice } from "@reduxjs/toolkit";
import {
  fetchAllSoftwareVersions,
  fetchTargetSoftware,
} from "../../thunks/targetSoftwareThunks";
import type { SoftwareVersionRow } from "../../thunks/targetSoftwareThunks";
import { SavingReducer } from "../general";

export type { SoftwareVersionRow } from "../../thunks/targetSoftwareThunks";

export type TargetSoftwareEntry = {
  value: string | undefined;
  /** Fetch lifecycle: saving = in flight, saved = data present, failed = request failed */
  status: SavingReducer;
};

export type AllSoftwareVersionsState = {
  list: SoftwareVersionRow[];
  status: SavingReducer;
};

export type TargetSoftwareState = {
  /** Current target from utils (per hub, read model). */
  bySerialId: Record<string, TargetSoftwareEntry>;
  /** Full catalog for the picker; not per machine. */
  allVersions: AllSoftwareVersionsState;
};

export const initialTargetSoftwareState: TargetSoftwareState = {
  bySerialId: {},
  allVersions: {
    list: [],
    status: "ready",
  },
};

const targetSoftwareSlice = createSlice({
  name: "targetSoftware",
  initialState: initialTargetSoftwareState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTargetSoftware.pending, (state, action) => {
        const id = action.meta.arg;
        const prev = state.bySerialId[id];
        state.bySerialId[id] = {
          value: prev?.value,
          status: "saving",
        };
      })
      .addCase(fetchTargetSoftware.fulfilled, (state, action) => {
        const { serialId, version } = action.payload;
        state.bySerialId[serialId] = {
          value: version,
          status: "saved",
        };
      })
      .addCase(fetchTargetSoftware.rejected, (state, action) => {
        const id = action.meta.arg;
        state.bySerialId[id] = {
          value: undefined,
          status: "failed",
        };
      });

    builder
      .addCase(fetchAllSoftwareVersions.pending, (state) => {
        state.allVersions = {
          list: state.allVersions.list,
          status: "saving",
        };
      })
      .addCase(fetchAllSoftwareVersions.fulfilled, (state, action) => {
        state.allVersions = {
          list: action.payload.list,
          status: "saved",
        };
      })
      .addCase(fetchAllSoftwareVersions.rejected, (state) => {
        state.allVersions = {
          list: [],
          status: "failed",
        };
      });
  },
});

export default targetSoftwareSlice.reducer;
